import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Menu,
    MenuItem
} from '@mui/material';
import { DownloadIcon, FileDownloadIcon } from 'icons/materialIcons';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { useParams } from 'react-router-dom';
import QRCodeStyling from 'qr-code-styling';
import { getCardById } from 'apis/cardApi';
import { useQRCodeContext } from 'contexts/QrCodeContext';
import jsPDF from 'jspdf';

const TeamQRCodePreview = () => {
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');
    const [cardFullName, setCardFullName] = useState('');
    const [cardDetails, setCardDetails] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);

    const qrRef = useRef(null);
    const containerRef = useRef(null);
    const downloadQrRef = useRef(null);

    const { cardId } = useParams();
    const { qrCodeState, initializeQRCode } = useQRCodeContext();
    const contextQrCodeColor = qrCodeState[cardId]?.contextQrCodeColor || '#000000';
    const contextQrCodeLogoUrl = qrCodeState[cardId]?.contextQrCodeLogoUrl || '';

    const BASE_URL = `${FRONTEND_BASE_URL}/card`;

    // ✅ FIXED: Memoize the cardUrl to prevent infinite loops
    // Only regenerate when cardId changes, not on every render
    const cardUrl = useMemo(() => {
        // Use cardId as a stable identifier instead of timestamp to prevent infinite loops
        // The cardId itself provides uniqueness, so we don't need a changing timestamp
        return `${BASE_URL}/${cardId}?source=qr_code`;
    }, [BASE_URL, cardId]);

    // ✅ FIXED: Remove console.log that was causing excessive logging
    // Create display QR code
    useEffect(() => {
        const initializeQR = async () => {
            try {
                if (containerRef.current) {
                    containerRef.current.innerHTML = '';

                    qrRef.current = new QRCodeStyling({
                        width: 200,
                        height: 200,
                        type: "png",
                        image: contextQrCodeLogoUrl,
                        data: cardUrl,
                        dotsOptions: {
                            color: contextQrCodeColor,
                            type: "square"
                        },
                        cornersSquareOptions: {
                            type: "square"
                        },
                        cornersDotOptions: {
                            type: "square"
                        },
                        backgroundOptions: {
                            color: "#ffffff"
                        },
                        imageOptions: {
                            crossOrigin: "anonymous",
                            margin: 7,
                            imageSize: 0.5,
                            hideBackgroundDots: true,
                        },
                        qrOptions: {
                            errorCorrectionLevel: "H"
                        },
                    });

                    await qrRef.current.append(containerRef.current);
                    setLoading(false);
                }
            } catch (err) {
                console.error('QR Code initialization error:', err);
                setError('Failed to generate QR code');
                setLoading(false);
            }
        };

        initializeQR();

    }, [cardUrl, contextQrCodeColor, contextQrCodeLogoUrl]);

    // Initialize QR code context
    useEffect(() => {
        if (cardId) {
            initializeQRCode(cardId);
        }
    }, [cardId, initializeQRCode]);

    // Fetch card details
    useEffect(() => {
        const fetchCardDetails = async () => {
            try {
                const data = await getCardById(cardId);
                setCardDetails(data);
                if (data?.card?.contact_info?.fullName) {
                    setCardFullName(data.card.contact_info.fullName);
                }
            } catch (error) {
                console.error('Error fetching card details:', error);
                setError('Failed to load card details');
            }
        };

        if (cardId) {
            fetchCardDetails();
        }
    }, [cardId]);

    const handleClick = (event) => {
        if (!downloading) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        if (!downloading) {
            setAnchorEl(null);
        }
    };

    // Create download QR instance
    useEffect(() => {
        downloadQrRef.current = new QRCodeStyling({
            width: 1000,
            height: 1000,
            type: "svg",
            data: cardUrl,
            image: contextQrCodeLogoUrl,
            dotsOptions: {
                color: contextQrCodeColor,
                type: "square"
            },
            cornersSquareOptions: {
                type: "square"
            },
            cornersDotOptions: {
                type: "square"
            },
            backgroundOptions: {
                color: "#ffffff"
            },
            imageOptions: {
                crossOrigin: "anonymous",
                margin: 7,
                imageSize: 0.4,
                hideBackgroundDots: true,
            },
            qrOptions: {
                errorCorrectionLevel: "H"
            },
        });
    }, [cardUrl, contextQrCodeColor, contextQrCodeLogoUrl]);

    const handleDownload = async (format) => {
        if (downloadQrRef.current && !downloading) {
            setDownloading(true);
            handleClose();

            try {

                // await new Promise(resolve => setTimeout(resolve, 5000));

                const fileName = `qr-code-${cardId}`;

                if (format === 'pdf') {
                    const pngData = await downloadQrRef.current.getRawData('png');
                    const reader = new FileReader();

                    reader.readAsDataURL(pngData);
                    reader.onloadend = () => {
                        const base64data = reader.result;

                        const pdf = new jsPDF({
                            orientation: 'portrait',
                            unit: 'mm',
                            format: 'a4'
                        });

                        const pageWidth = pdf.internal.pageSize.getWidth();
                        const pageHeight = pdf.internal.pageSize.getHeight();
                        const qrSize = 80;
                        const x = (pageWidth - qrSize) / 2;
                        const y = 20;

                        pdf.addImage(base64data, 'PNG', x, y, qrSize, qrSize);
                        pdf.save(`${fileName}.pdf`);
                        setDownloading(false);
                    };
                } else if (format === 'svg') {
                    const svgData = await downloadQrRef.current.getRawData('svg');
                    const svgText = await svgData.text();

                    // Safely set width and height to 200 without duplicating viewBox
                    let wrappedSvg = svgText
                        .replace(/width="[^"]+"/, 'width="200"')
                        .replace(/height="[^"]+"/, 'height="200"');
                    
                    if (!wrappedSvg.includes('viewBox')) {
                        wrappedSvg = wrappedSvg.replace('<svg ', '<svg viewBox="0 0 1000 1000" ');
                    }

                    const blob = new Blob([wrappedSvg], { type: 'image/svg+xml' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${fileName}.svg`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    setDownloading(false);
                } else {
                    await downloadQrRef.current.download({
                        extension: format,
                        name: fileName,
                    });
                    setDownloading(false);
                }
            } catch (error) {
                console.error('Error downloading QR code:', error);
                setError('Failed to download QR code');
                setDownloading(false);
            }
        }
    };



    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
                maxWidth: '500px',
                margin: '20px',
            }}
        >
            {loading && <CircularProgress />}

            {error && (
                <Typography variant="body1" color="error">
                    {error}
                </Typography>
            )}

            <Box textAlign="center">
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        color: '#828282',
                    }}
                >
                    {cardFullName ? `${cardFullName}'s QRCode` : 'QR Code'}
                </Typography>
                <Box
                    ref={containerRef}
                    sx={{
                        marginTop: '40px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '300px',
                        '& svg': {
                            transition: 'all 0.3s ease-in-out',
                        }
                    }}
                />

                {/* Download Button and Menu */}
                <Box sx={{ mt: 3, position: 'relative' }}>
                    <Button
                        variant="contained"
                        startIcon={downloading ? null : <DownloadIcon />}
                        onClick={handleClick}
                        disabled={downloading}
                        sx={{
                            backgroundColor: '#4f4f4f',
                            '&:hover': {
                                backgroundColor: '#666666',
                            },
                            textTransform: 'none',
                            px: 3,
                            py: 1,
                            minWidth: '180px'
                        }}
                    >
                        {downloading ? (
                            <>
                                <CircularProgress
                                    size={16}
                                    sx={{
                                        color: '#2196f3',
                                        marginRight: 1
                                    }}
                                />
                                Downloading...
                            </>
                        ) : (
                            'Download QR Code'
                        )}
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && !downloading}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <MenuItem onClick={() => handleDownload('png')} sx={{ gap: 1 }}>
                            <FileDownloadIcon fontSize="small" />
                            Download as PNG
                        </MenuItem>
                        <MenuItem onClick={() => handleDownload('svg')} sx={{ gap: 1 }}>
                            <FileDownloadIcon fontSize="small" />
                            Download as SVG
                        </MenuItem>
                        <MenuItem onClick={() => handleDownload('pdf')} sx={{ gap: 1 }}>
                            <FileDownloadIcon fontSize="small" />
                            Download as PDF
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>
        </Box>
    );
};

export default TeamQRCodePreview;